import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Scissors, Ruler, Sparkles } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { PriceList } from "@/components/site/PriceList";
import { useContent } from "@/context/ContentContext";
import { IMG } from "@/lib/images";
import { SeasonBanner } from "@/components/site/SeasonBanner";
import { FaqSection } from "@/components/site/Faq";

const Hero = () => (
  <section className="relative grain overflow-hidden px-6 md:px-16 lg:px-24 pt-12 md:pt-20 pb-24" data-testid="hero-section">
    <div className="grid lg:grid-cols-12 gap-12 items-center">
      <motion.div className="lg:col-span-6 relative z-10" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
        <p className="eyebrow mb-6">Skredderatelier i Bergen</p>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.98]">
          Klær som sitter <em className="text-[#8C6E48] font-normal">perfekt</em>, sydd med kjærlighet til det unike.
        </h1>
        <p className="mt-8 max-w-lg text-base md:text-lg text-[#5C5A56] leading-relaxed">
          Tilpasning, reparasjon, skreddersøm og brudekjoler – og personlige sykurs for deg som vil lære selv.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/bestill" className="px-8 py-4 bg-[#1A1A1A] text-[#FDFBF7] text-sm tracking-wide hover:bg-[#8C6E48] transition-colors duration-300" data-testid="hero-book-btn">Bestill tid</Link>
          <Link to="/bestill?type=pristilbud#forespørsel" className="px-8 py-4 border border-[#C4A47C] text-[#1A1A1A] text-sm tracking-wide hover:bg-[#C4A47C] hover:text-white transition-colors duration-300" data-testid="hero-quote-btn">Be om pristilbud</Link>
          <Link to="/sykurs" className="group flex items-center gap-2 px-2 py-4 text-sm tracking-wide" data-testid="hero-courses-link">
            Velg sykurs <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
          </Link>
        </div>
      </motion.div>
      <div className="lg:col-span-6 relative h-[480px] md:h-[620px]">
        <motion.img src={IMG.heroBridal} alt="Brudekjole under tilpasning" className="absolute right-0 top-0 w-[78%] h-[88%] object-cover" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4 }} />
        <motion.img src={IMG.tools} alt="Målebånd og skredderverktøy" className="absolute left-0 bottom-0 w-[46%] h-[48%] object-cover border-8 border-[#FDFBF7]" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.3 }} />
        <div className="absolute right-6 top-6 bg-[#FDFBF7] px-5 py-4 border border-[#E5E0D8] hidden md:block">
          <p className="font-serif-display text-2xl italic">Gratis prøvetime</p>
          <p className="text-xs text-[#5C5A56] mt-1">10–15 minutter – vi ser på plagget sammen</p>
        </div>
      </div>
    </div>
  </section>
);

const SERVICES = [
  { to: "/tjenester", title: "Tilpasning & reparasjon", text: "Bukser, jakker, kjoler og kåper – opplegg, innsying og nye glidelåser.", img: IMG.alterWomen, cls: "md:col-span-7 md:row-span-2 h-[420px] md:h-full", icon: Scissors },
  { to: "/brudekjoler", title: "Brudekjoler & festantrekk", text: "To veier til drømmekjolen.", img: IMG.festwear, cls: "md:col-span-5 h-[320px]", icon: Sparkles },
  { to: "/tjenester#skreddersom", title: "Skreddersøm etter mål", text: "Unike plagg designet for deg.", img: IMG.alterMen, cls: "md:col-span-5 h-[320px]", icon: Ruler },
];

const ServicesBento = () => (
  <section className="px-6 md:px-16 lg:px-24 py-24" data-testid="services-section">
    <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
      <div>
        <p className="eyebrow mb-4">Tjenester</p>
        <h2 className="text-4xl md:text-5xl font-medium tracking-tight max-w-xl">Håndverk for hvert plagg i garderoben</h2>
      </div>
      <Link to="/tjenester" className="group flex items-center gap-2 text-sm" data-testid="services-all-link">Alle tjenester <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} /></Link>
    </Reveal>
    <div className="grid md:grid-cols-12 md:grid-rows-2 gap-6 md:h-[680px]">
      {SERVICES.map((s, i) => (
        <Link key={s.title} to={s.to} className={`group relative overflow-hidden ${s.cls}`} data-testid={`service-card-${i}`}>
          <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-[1200ms]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/10 to-transparent" />
          <div className="absolute bottom-0 left-0 p-8 text-[#FDFBF7]">
            <s.icon className="w-6 h-6 mb-4 text-[#C4A47C]" strokeWidth={1.2} />
            <h3 className="text-3xl md:text-4xl font-medium">{s.title}</h3>
            <p className="text-sm mt-2 text-[#E5E0D8] max-w-sm">{s.text}</p>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const BridalPaths = () => (
  <section className="bg-[#F0EBE1] px-6 md:px-16 lg:px-24 py-24" data-testid="bridal-section">
    <div className="grid lg:grid-cols-12 gap-14 items-center">
      <Reveal className="lg:col-span-5">
        <img src={IMG.bridalPoster} alt="Skreddersydde brudekjoler – to unike veier" className="w-full max-h-[640px] object-cover object-top" />
      </Reveal>
      <div className="lg:col-span-7">
        <Reveal>
          <p className="eyebrow mb-4">Brudekjoler</p>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight">To unike veier til drømmekjolen</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-10">
          {[
            ["01", "Sy din egen brudekjole", "Skap kjolen etter eget design, lær å sy under veiledning og følg hele prosessen i ditt eget tempo. Hver time avtales individuelt."],
            ["02", "Få kjolen skreddersydd", "Utvikle designet sammen med Liisa og få en unik brudekjole som ingen andre har maken til."],
          ].map(([n, t, d], i) => (
            <Reveal key={n} delay={i * 0.15}>
              <p className="font-serif-display text-6xl text-[#C4A47C]">{n}</p>
              <h3 className="text-2xl font-medium mt-3">{t}</h3>
              <p className="mt-3 text-sm text-[#5C5A56] leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3}>
          <Link to="/bestill?type=brudekjole#forespørsel" className="inline-block mt-12 px-8 py-4 bg-[#1A1A1A] text-[#FDFBF7] text-sm tracking-wide hover:bg-[#8C6E48] transition-colors duration-300" data-testid="bridal-contact-btn">Ta kontakt om brudekjole</Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const BunadBand = () => (
  <section className="px-6 md:px-16 lg:px-24 py-24" data-testid="bunad-section">
    <div className="grid lg:grid-cols-12 gap-14 items-center">
      <Reveal className="lg:col-span-6 lg:order-2">
        <img src={IMG.bunad} alt="Tilpasning av bunad" className="w-full h-[560px] object-cover" />
      </Reveal>
      <Reveal className="lg:col-span-5" delay={0.1}>
        <p className="eyebrow mb-4">Bunad</p>
        <h2 className="text-4xl md:text-5xl font-medium tracking-tight">Klar til 17. mai, konfirmasjon, jul og fest</h2>
        <p className="mt-6 text-[#5C5A56] leading-relaxed">Vi tilpasser og justerer alle typer bunader – nye, arvede og herrebunader – med respekt for håndverket og tradisjonen. Kom til en gratis konsultasjon, så ser vi på bunaden sammen.</p>
        <p className="mt-4 text-sm text-[#8C6E48] italic">Haster det? Ekspress innen 24 timer etter avtale, avhengig av kapasitet og arbeidets omfang. Tillegg: 40 %.</p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/bestill?type=bunad#forespørsel" className="px-8 py-4 bg-[#1A1A1A] text-[#FDFBF7] text-sm tracking-wide hover:bg-[#8C6E48] transition-colors duration-300" data-testid="bunad-quote-btn">Spør om bunad</Link>
          <Link to="/bunad" className="group flex items-center gap-2 px-2 py-4 text-sm" data-testid="bunad-read-more">Les mer <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} /></Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const CoursesTeaser = ({ courses }) => (
  <section className="px-6 md:px-16 lg:px-24 py-24" data-testid="courses-teaser">
    <div className="grid lg:grid-cols-12 gap-14">
      <Reveal className="lg:col-span-4">
        <p className="eyebrow mb-4">Sykurs</p>
        <h2 className="text-4xl md:text-5xl font-medium tracking-tight">Lær å sy – med personlig veiledning</h2>
        <img src={IMG.machine} alt="Sykurs" className="mt-10 w-full h-72 object-cover" />
      </Reveal>
      <div className="lg:col-span-8 divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
        {courses.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.08}>
            <Link to="/sykurs" className="group flex items-center justify-between gap-6 py-8" data-testid={`course-teaser-${i}`}>
              <div>
                <p className="text-xs tracking-[0.18em] uppercase text-[#8C6E48]">{c.level} · {c.duration}</p>
                <h3 className="text-2xl md:text-3xl font-medium mt-2 group-hover:text-[#8C6E48] transition-colors">{c.title}</h3>
              </div>
              <ArrowRight className="w-5 h-5 shrink-0 group-hover:translate-x-2 transition-transform" strokeWidth={1.2} />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PricesTeaser = ({ services }) => (
  <section className="px-6 md:px-16 lg:px-24 py-24 bg-[#FDFBF7] border-t border-[#E5E0D8]" data-testid="prices-teaser">
    <Reveal className="mb-14">
      <p className="eyebrow mb-4">Priser høsten 2026</p>
      <h2 className="text-4xl md:text-5xl font-medium tracking-tight">Et utdrag fra prislisten</h2>
    </Reveal>
    <PriceList services={services} limitCategories={2} />
    <Link to="/priser" className="group inline-flex items-center gap-2 mt-12 text-sm border-b border-[#1A1A1A] pb-1" data-testid="prices-all-link">Se hele prislisten <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} /></Link>
  </section>
);

export default function Home() {
  const c = useContent();
  return (
    <div data-testid="home-page">
      <SeasonBanner />
      <Hero />
      <ServicesBento />
      <BridalPaths />
      <BunadBand />
      {c && <CoursesTeaser courses={c.courses} />}
      {c && <PricesTeaser services={c.services} />}
      <FaqSection />
    </div>
  );
}
